import { useMemo, type ReactNode } from "react";
import { resolveLocale } from "../../utils/resolveLocale";

// PageHeader is now theme-agnostic — both editorial-light and editorial-dark
// share the same eyebrow + display + subtitle composition. Theme-specific
// styling lives in CSS via the `[data-theme="..."]` selectors.
//
// Locale resolves via the shared `utils/resolveLocale` helper so Overview +
// StatsBar + PageHeader never diverge on the resolution chain.

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** When true the eyebrow shows "Live" (with "· refreshing" while fetching). */
  live?: boolean;
  /**
   * Accepted for backwards-compat with callers that still pass it; the
   * "Updated X ago" row was removed for cross-page visual parity, so the
   * value is no longer rendered.
   */
  updatedAt?: Date | number;
  /** True while a background refetch is in flight — flips eyebrow to "Live · refreshing". */
  isFetching?: boolean;
  /** Backwards-compat — staleness is no longer surfaced in the header. */
  isStale?: boolean;
  /** Backwards-compat — staleness is no longer surfaced in the header. */
  staleAfterMs?: number;
  actions?: ReactNode;
  recordCount?: number;
  recordLabel?: string;
  className?: string;
  /**
   * Suppress the eyebrow row entirely. Detail pages (`/agents/:agentId`,
   * `/policies/:policyId`, `/incidents/:id`, …) drill into a single
   * entity — the parent listing page already carries the section
   * eyebrow, so a second eyebrow on the detail view is redundant.
   * Default false so list / overview pages keep cross-page parity.
   */
  noEyebrow?: boolean;
  /**
   * Optional tab row. When provided it renders as the header's bottom edge and
   * REPLACES the header's own divider — so a tabbed hub page shows a single
   * underline instead of header-border + tablist-border (the double line).
   * Pass the full `<div role="tablist">…</div>` (it carries its own border-b).
   */
  tabs?: ReactNode;
}

export function PageHeader({
  title,
  subtitle,
  live,
  isFetching,
  actions,
  recordCount,
  recordLabel = "records",
  className = "",
  noEyebrow = false,
  tabs,
}: PageHeaderProps) {
  // Resolve once per mount. Single source so locale formatting stays consistent
  // within a single session.
  const locale = useMemo(() => resolveLocale(), []);

  // Cross-page visual parity: every page renders an "eyebrow strip" at the very
  // top of the header — a small uppercase mono row above the h1 title. Pages
  // with live telemetry populate it with "Live · 47 traces"; config pages leave
  // it empty (height preserved via a non-breaking space). Subtitle ALWAYS
  // renders below the title.
  const eyebrowParts: string[] = [];
  if (live) eyebrowParts.push(isFetching ? "Live · refreshing" : "Live");
  if (recordCount !== undefined) {
    eyebrowParts.push(`${recordCount.toLocaleString(locale)} ${recordLabel}`);
  }
  const eyebrowText = eyebrowParts.join(" · ");

  const inner = (
    <>
      <div className="flex-1 min-w-0">
        {/* Empty eyebrow collapses to a non-breaking space so the row → title →
            subtitle rhythm stays consistent across pages. Detail pages opt out
            via `noEyebrow` since the parent already carries the eyebrow. */}
        {!noEyebrow && (
          <div className="editorial-eyebrow">{eyebrowText || " "}</div>
        )}
        <h1 className="editorial-display">{title}</h1>
        {subtitle && (
          <p className="editorial-subtitle">{subtitle}</p>
        )}
      </div>

      {actions && (
        <div className="flex items-center gap-2 flex-shrink-0 pt-2">
          {actions}
        </div>
      )}
    </>
  );

  // Tabbed hub page: header block (no own divider) + the tab row as the single
  // bottom edge. The tablist carries its own border-b → one line, not two.
  if (tabs) {
    return (
      <div className={className}>
        <div className="flex items-start justify-between gap-6 flex-wrap" style={{ paddingBottom: "16px" }}>
          {inner}
        </div>
        {tabs}
      </div>
    );
  }

  return (
    <div className={`border-b border-border flex items-start justify-between gap-6 flex-wrap ${className}`}
         style={{ paddingBottom: "20px" }}>
      {inner}
    </div>
  );
}
